/**
 * AdminInstructorsTab - Instructor management tab for admin panel
 * Self-contained component for managing instructors
 * Extracted from AdminPage.js for better maintainability
 */
import React, { useState, useEffect } from 'react';
import { 
  Trash2, Pencil, Plus, Loader2, Eye, EyeOff, ArrowRightLeft, Users
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';

import { API_URL } from '../../utils/api';

export function AdminInstructorsTab({ onDataChange }) {
  // State
  const [instructors, setInstructors] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal state
  const [showInstructorModal, setShowInstructorModal] = useState(false);
  const [editingInstructor, setEditingInstructor] = useState(null);
  const [instructorFormData, setInstructorFormData] = useState({
    name: '',
    bio: '',
    email: '',
    phone: '',
    image_url: '',
    specialties: [],
    club_id: ''
  });
  const [saving, setSaving] = useState(false);
  const [specialtyInput, setSpecialtyInput] = useState('');
  
  // Transfer modal
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferTarget, setTransferTarget] = useState(null);
  const [transferToUserId, setTransferToUserId] = useState('');
  const [transferring, setTransferring] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    fetchInstructors();
    fetchClubs();
    fetchUsers();
  }, []);

  const fetchInstructors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/instructors`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setInstructors(data);
      }
    } catch (error) {
      console.error('Error fetching instructors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClubs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/clubs`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setClubs(data);
      }
    } catch (error) {
      console.error('Error fetching clubs:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const openInstructorModal = (instructor = null) => {
    setEditingInstructor(instructor);
    if (instructor) {
      setInstructorFormData({
        name: instructor.name || '',
        bio: instructor.bio || '',
        email: instructor.email || '',
        phone: instructor.phone || '',
        image_url: instructor.image_url || '',
        specialties: instructor.specialties || [],
        club_id: instructor.club_id || ''
      });
    } else {
      setInstructorFormData({
        name: '',
        bio: '',
        email: '',
        phone: '',
        image_url: '',
        specialties: [],
        club_id: ''
      });
    }
    setSpecialtyInput('');
    setShowInstructorModal(true);
  };

  const handleSaveInstructor = async () => {
    if (!instructorFormData.name) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = editingInstructor 
        ? `${API_URL}/api/admin/instructors/${editingInstructor.instructor_id}`
        : `${API_URL}/api/admin/instructors`;
      
      const response = await fetch(url, {
        method: editingInstructor ? 'PUT' : 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(instructorFormData)
      });

      if (response.ok) {
        toast.success(editingInstructor ? 'Instructor updated' : 'Instructor added');
        setShowInstructorModal(false);
        fetchInstructors();
        if (onDataChange) onDataChange();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to save instructor');
      }
    } catch (error) {
      toast.error('Error saving instructor');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteInstructor = async (instructorId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/instructors/${instructorId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Instructor deleted');
        fetchInstructors();
        if (onDataChange) onDataChange();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete instructor');
      }
    } catch (error) {
      toast.error('Error deleting instructor');
    }
  };

  const handleToggleInstructorVisibility = async (instructorId, isHidden) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/instructors/${instructorId}/visibility`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ is_hidden: !isHidden })
      });

      if (response.ok) {
        toast.success(isHidden ? 'Instructor visible' : 'Instructor hidden');
        fetchInstructors();
      } else {
        toast.error('Failed to update visibility');
      }
    } catch (error) {
      toast.error('Error updating visibility');
    }
  };

  const openTransferModal = (instructor) => {
    setTransferTarget(instructor);
    setTransferToUserId('');
    setShowTransferModal(true);
  };

  const handleTransferOwnership = async () => {
    if (!transferTarget || !transferToUserId) {
      toast.error('Please select a user');
      return;
    }

    setTransferring(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/instructors/${transferTarget.instructor_id}/transfer`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ new_owner_id: transferToUserId })
      });

      if (response.ok) {
        toast.success('Ownership transferred');
        setShowTransferModal(false);
        fetchInstructors();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to transfer ownership');
      }
    } catch (error) {
      toast.error('Error transferring ownership');
    } finally {
      setTransferring(false);
    }
  };

  const addSpecialty = () => {
    if (specialtyInput.trim() && !instructorFormData.specialties.includes(specialtyInput.trim())) {
      setInstructorFormData(prev => ({
        ...prev,
        specialties: [...prev.specialties, specialtyInput.trim()]
      }));
      setSpecialtyInput('');
    }
  };

  const removeSpecialty = (specialty) => {
    setInstructorFormData(prev => ({
      ...prev,
      specialties: prev.specialties.filter(s => s !== specialty)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="flex justify-between items-center px-6 py-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">All Instructors</h3>
          <Button 
            onClick={() => openInstructorModal()}
            className="bg-violet-600 hover:bg-violet-500"
            data-testid="add-instructor-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Instructor
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Instructor</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Specialties</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Linked Club</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Contact</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {instructors.map(instructor => (
                <tr key={instructor.instructor_id} className={`border-b border-slate-100 hover:bg-slate-50 ${instructor.is_hidden ? 'opacity-50' : ''}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center overflow-hidden">
                        {instructor.image_url ? (
                          <img src={instructor.image_url} alt={instructor.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-violet-600 font-bold">
                            {instructor.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{instructor.name}</p>
                          {instructor.is_hidden && (
                            <Badge variant="secondary" className="bg-red-100 text-red-700 text-xs">Hidden</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 line-clamp-1 max-w-xs">{instructor.bio}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {instructor.specialties?.slice(0, 3).map((s, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>
                      ))}
                      {instructor.specialties?.length > 3 && (
                        <Badge variant="secondary" className="text-xs">+{instructor.specialties.length - 3}</Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {instructor.club_name ? (
                      <Badge className="bg-violet-100 text-violet-700">{instructor.club_name}</Badge>
                    ) : (
                      <span className="text-sm text-slate-500">Not linked</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {instructor.email && <div>{instructor.email}</div>}
                    {instructor.phone && <div>{instructor.phone}</div>}
                    {!instructor.email && !instructor.phone && <span className="text-slate-500">-</span>}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleInstructorVisibility(instructor.instructor_id, instructor.is_hidden)}
                        title={instructor.is_hidden ? "Show instructor" : "Hide instructor"}
                        data-testid={`toggle-visibility-instructor-${instructor.instructor_id}`}
                      >
                        {instructor.is_hidden ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-slate-500" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openTransferModal(instructor)}
                        title="Transfer ownership"
                        data-testid={`transfer-instructor-${instructor.instructor_id}`}
                      >
                        <ArrowRightLeft className="w-4 h-4 text-blue-500" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openInstructorModal(instructor)}
                        data-testid={`edit-instructor-${instructor.instructor_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" data-testid={`delete-instructor-${instructor.instructor_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Instructor</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete {instructor.name}? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeleteInstructor(instructor.instructor_id)} className="bg-red-600 hover:bg-red-700">
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {instructors.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            No instructors found. Add your first instructor to get started.
          </div>
        )}
      </div>

      {/* Add/Edit Instructor Modal */}
      <Dialog open={showInstructorModal} onOpenChange={setShowInstructorModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingInstructor ? 'Edit Instructor' : 'Add Instructor'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div>
              <label className="text-sm font-medium text-slate-700">Name *</label>
              <Input
                value={instructorFormData.name}
                onChange={(e) => setInstructorFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Full name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Bio</label>
              <Textarea
                value={instructorFormData.bio}
                onChange={(e) => setInstructorFormData(prev => ({ ...prev, bio: e.target.value }))}
                rows={3}
                placeholder="Brief biography..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Email</label>
                <Input
                  type="email"
                  value={instructorFormData.email}
                  onChange={(e) => setInstructorFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Phone</label>
                <Input
                  value={instructorFormData.phone}
                  onChange={(e) => setInstructorFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Phone number"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Image URL</label>
              <Input
                value={instructorFormData.image_url}
                onChange={(e) => setInstructorFormData(prev => ({ ...prev, image_url: e.target.value }))}
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Link to Club</label>
              <Select 
                value={instructorFormData.club_id || "none"} 
                onValueChange={(val) => setInstructorFormData(prev => ({ ...prev, club_id: val === "none" ? "" : val }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a club" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No club</SelectItem>
                  {clubs.map(club => (
                    <SelectItem key={club.club_id} value={club.club_id}>{club.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Specialties</label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={specialtyInput}
                  onChange={(e) => setSpecialtyInput(e.target.value)}
                  placeholder="Add specialty"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSpecialty())}
                />
                <Button type="button" variant="outline" onClick={addSpecialty}>Add</Button>
              </div>
              {instructorFormData.specialties.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {instructorFormData.specialties.map((s, i) => (
                    <Badge key={i} variant="secondary" className="pr-1">
                      {s}
                      <button 
                        type="button"
                        onClick={() => removeSpecialty(s)}
                        className="ml-1 hover:text-red-600"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInstructorModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveInstructor} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingInstructor ? 'Update Instructor' : 'Add Instructor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Ownership Modal */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer Ownership</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Transfer ownership of <strong>{transferTarget?.name}</strong> to another user.
            </p>
            <div>
              <label className="text-sm font-medium text-slate-700">New Owner</label>
              <Select value={transferToUserId} onValueChange={setTransferToUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.user_id} value={user.user_id}>
                      {user.name || user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleTransferOwnership} disabled={transferring || !transferToUserId}>
              {transferring ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Transfer Ownership
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AdminInstructorsTab;
